/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  Card,
  DetailStack, Message, Text,
} from '@doveit/bricks';
import { formatEuro, formatFloor, formatSquareMeters } from '@doveit/hammer';
import { CardHeaderProps } from '@doveit/bricks/dist/typings/components/card/composition';
import { Property, PropertyPreview } from '../../../providers/api/dtos';
import PropertyAvatar from '../../components/property-avatar/PropertyAvatar';
import * as styles from './ViewPropertyPreview.style';
import { countRooms, NO_VALUE_SYMBOL } from '../../utils';
import useRooms from '../../hooks/use-rooms/useRooms';
import EntityReferenceBadge from '../../../components/entity-reference-badge/EntityReferenceBadge';
import { ReferenceType, Status } from '../../../domain/types';
import usePropertyPreview from '../../hooks/use-property-preview/usePropertyPreview';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import useAssignmentByPropertyId from '../../hooks/use-assignment-by-property-id/useAssignmentByPropertyId';
import useProspect from '../../../prospect/hooks/use-prospect/useProspect';
import useLead from '../../../lead/hooks/use-lead/useLead';
import ViewEntityPreviewSkeleton from '../../../components/view-entity-preview-skeleton/ViewEntityPreviewSkeleton';

interface ViewPropertyPreviewBaseProps extends React.AriaAttributes, Pick<CardHeaderProps, 'title' | 'primaryActions' | 'secondaryActions'> {
  onClick?: (property: PropertyPreview) => void,
}

export type ViewPropertyPreviewProps = ViewPropertyPreviewBaseProps & ({
  propertyId: NonNullable<Property['id']>,
  property?: undefined,
} | {
  property: PropertyPreview,
  propertyId?: undefined,
});

export const LOAD_PROPERTY_DATA_ERROR_MESSAGE = 'Non è stato possibile caricare i dati dell\'immobile';

const ViewPropertyPreview: React.FC<ViewPropertyPreviewProps> = ({
  propertyId,
  title = 'Immobile',
  primaryActions,
  secondaryActions,
  property: externalProperty,
  onClick,
  ...rest
}) => {
  const isMobile = useIsDevice('mobile');
  const { data: loadedProperty, isLoading: isPropertyLoading, error: propertyError } = usePropertyPreview(propertyId);

  const property = externalProperty || loadedProperty;
  const { data: assignment } = useAssignmentByPropertyId(property?.id);
  const { data: prospect } = useProspect(assignment?.prospectId);
  const { data: lead } = useLead(prospect?.leadId);

  const { data: rooms, isLoading: isRoomsLoading, error: roomsError } = useRooms(property?.id);

  const propertyAddress = React.useMemo(() => (
    property?.overriddenGeo
      ? [property.overriddenGeo?.route, property.overriddenGeo?.streetNumber]
      : [property?.geo?.route, property?.geo?.streetNumber]).filter((value) => !!value).join(', '), [property]);

  const propertyPrice = React.useMemo(() => {
    if (property?.status === Status.VENDUTO && property?.soldPrice) {
      return formatEuro(property?.soldPrice);
    }

    return property?.price ? formatEuro(property.price) : NO_VALUE_SYMBOL;
  }, [property]);

  const features = React.useMemo(() => ([
    {
      label: 'Superficie',
      value: property?.propertySize ? formatSquareMeters(property.propertySize) : NO_VALUE_SYMBOL,
      width: '3.75rem',
    },
    {
      label: 'Locali',
      value: rooms ? countRooms(rooms) : NO_VALUE_SYMBOL,
    },
    {
      label: 'Piano',
      value: property?.mainFloor !== undefined ? formatFloor(property?.mainFloor) : NO_VALUE_SYMBOL,
    },
  ]), [property, rooms]);

  if ((isPropertyLoading || isRoomsLoading) && !propertyError && !roomsError) {
    return <ViewEntityPreviewSkeleton aria-label="Informazioni immobile in caricamento" />;
  }

  if (propertyError || roomsError) {
    return (
      <Message
        type="critical"
        message={LOAD_PROPERTY_DATA_ERROR_MESSAGE}
      />
    );
  }

  return property && rooms && (
    <Card
      aria-label="Anteprima immobile"
      onClick={onClick ? () => onClick(property) : undefined}
      {...rest}
    >
      {(primaryActions || secondaryActions) && (
        <Card.Header
          aria-label="Anteprima immobile (header)"
          title={title}
          primaryActions={primaryActions}
          secondaryActions={secondaryActions}
        />
      )}
      <Card.Content>
        <div css={styles.basic}>
          <div css={styles.info}>
            <PropertyAvatar
              size={isMobile ? 'M' : 'L'}
              propertyType={property.propertyType}
              isPersonal={!!lead?.originAgentId}
            />
            <div css={styles.textInfo}>
              <Text.H4
                aria-label="Anteprima immobile (title)"
                lineHeight="1"
                as="div"
              >
                {propertyAddress}
              </Text.H4>
              <Text.Body
                lineHeight="1"
                as="div"
              >
                {property?.geo?.locality}
              </Text.Body>
            </div>
          </div>
          <div css={styles.detailsWrapper}>
            <div css={styles.details}>
              <div>
                <EntityReferenceBadge
                  id={property.id!}
                  size="XS"
                  entity={ReferenceType.PROPERTY}
                  label={property?.referenceId}
                  isNewTab
                />
              </div>
              <div>
                <DetailStack
                  label="Prezzo"
                  aria-label={'Anteprima dell\'immobile (prezzo)'}
                  size="M"
                  width={isMobile ? undefined : '5rem'}
                >
                  {propertyPrice}
                </DetailStack>
              </div>
              {features.map((feature) => (
                <div key={`${feature.label}-${feature.value}`}>
                  <DetailStack
                    label={feature.label}
                    aria-label={`${feature.label} dell'immobile`}
                    size="M"
                    width={!isMobile && feature.width ? feature.width : undefined}
                  >
                    {feature.value}
                  </DetailStack>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card.Content>
    </Card>
  );
};

export default ViewPropertyPreview;
